import { isAxiosError } from 'axios'
import { DateTime } from 'luxon'
import { DATE_FORMATS_LUXON } from '@/utils/dateHelpers'

export interface WrongPeriodRejection {
  closeMonth: string
  targetMonth: string
  suggestedReportId: number | null
}

export const parseWrongPeriodRejection = (
  err: unknown
): WrongPeriodRejection | null => {
  if (!isAxiosError(err)) return null

  const data = err.response?.data
  if (data?.code !== 'wrong_period') return null

  return {
    closeMonth: data.closeMonth,
    targetMonth: data.targetMonth,
    suggestedReportId: data.suggestedReportId ?? null,
  }
}

/**
 * Local, not UTC: a UTC-derived yesterday reads as today between UTC midnight and local
 * midnight. Called at render rather than module load so a tab left open overnight does
 * not hold a stale day.
 */
export const localYesterday = () =>
  DateTime.now().minus({ days: 1 }).startOf('day')

/** True when a local calendar day falls inside a 'yyyy-MM' bookkeeping month. */
export const isDayInReportMonth = (day: DateTime, reportMonth: string) =>
  day.toFormat(DATE_FORMATS_LUXON.YEAR_MONTH) === reportMonth

/**
 * The ending dates the server accepts. `reportMonths` is every month a write from this
 * form could land under — the viewed report, plus the surfaced row's own report where the
 * form retargets to it. `exemptClose` is an existing row's own close, which a write may
 * leave exactly where it is. Those sets are disjoint whenever a row straddles, so the
 * picker needs `isSelectable` on top of `latest`, not a range alone.
 */
export const endingDateBounds = ({
  reportMonths,
  periodStart,
  exemptClose,
}: {
  reportMonths: string[]
  periodStart: DateTime | null
  exemptClose: DateTime | null
}) => {
  const months = [...new Set(reportMonths)].map((month) => {
    const start = DateTime.fromFormat(
      month,
      DATE_FORMATS_LUXON.YEAR_MONTH
    ).startOf('month')
    return { month, start, end: start.endOf('month') }
  })

  const furthest = [
    ...months.map(({ end }) => end),
    ...(exemptClose ? [exemptClose] : []),
  ].reduce((a, b) => (b > a ? b : a))
  const latest = DateTime.min(localYesterday(), furthest)

  const isSelectable = (date: Date) => {
    const day = DateTime.fromJSDate(date)
    return (
      months.some(({ month }) => isDayInReportMonth(day, month)) ||
      (exemptClose !== null && day.hasSame(exemptClose, 'day'))
    )
  }

  // Reported separately because the two halves are offered for different reasons, and
  // prose that names an option the picker greys out reads as a bug.
  const monthAvailable =
    periodStart === null ||
    months.some(({ start, end }) => start <= latest && end >= periodStart)
  const exemptAvailable =
    exemptClose !== null &&
    (periodStart === null || exemptClose >= periodStart) &&
    exemptClose <= latest

  return {
    latest,
    isSelectable,
    monthAvailable,
    exemptAvailable,
    hasSelectable: monthAvailable || exemptAvailable,
  }
}

/** '2026-01' → 'January 2026', for prose aimed at a bookkeeper. */
export const formatReportMonth = (yearMonth: string) =>
  DateTime.fromFormat(yearMonth, DATE_FORMATS_LUXON.YEAR_MONTH, {
    zone: 'utc',
  }).toFormat(DATE_FORMATS_LUXON.MONTH_YEAR_LONG)

export const wrongPeriodMessage = ({
  closeMonth,
  suggestedReportId,
}: WrongPeriodRejection) =>
  suggestedReportId === null
    ? `This statement closes in ${formatReportMonth(closeMonth)}, which has no bookkeeping report yet. Generate books for that month first.`
    : `This statement closes in ${formatReportMonth(closeMonth)}, so it belongs to that month's books.`
